import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useIsraeliVatEligibility} from 'hooks/useIsraeliVatEligibility'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {getHotelResult} from 'modules/searchApi/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {PopUp} from '@vio/atlas/PopUp'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {defaultCurrencyCode} from '@vio/core/src/localization/business/currency'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {getDisplayPrice} from '@vio/core/src/price/business/price'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {
  RoomWithoutOffers,
  SplitBookingDates
} from '@vio/core/src/room/types/room'
import {calculateDateDifference} from '@vio/core/src/utils/date'
import {PriceDetails} from '@vio/shared/src/booking/PriceDetails'
import {RoomPrice} from '@vio/shared/src/booking/RoomDetailsSection'

export interface PriceBreakdownModalProps {
  isOpen: boolean
  onClose: () => void
  prefersReducedMotion?: boolean
  prices: OfferPrice[]
  splitBookingRooms: RoomWithoutOffers[]
  splitBookingDates: SplitBookingDates
}

const Title = () => (
  <Text variant="titleM">
    <FormattedMessageWrapper
      id="priceBreakdown"
      defaultMessage="Price Breakdown"
    />
  </Text>
)

const RoomNamesWrapper = styled(Grid)`
  padding-bottom: ${cssTheme.layout.spacing.s300};
`

export const RoomsTopOfferPriceBreakdownModal = ({
  isOpen = false,
  onClose,
  prefersReducedMotion,
  prices,
  splitBookingDates,
  splitBookingRooms
}: PriceBreakdownModalProps) => {
  const {isMobile} = useDeviceLayout()

  const {rooms} = useSelector(getSearchBoxValues)
  const roomsConfig = roomsToConfigurationObject(rooms)

  const hotelId = useSelector(getAccommodationPageHotelId)
  const {data: hotel} = useSelector(state => getHotelResult(state, hotelId))

  const {isPropertyEligible, isLocalVatIncluded, hasAdditionalCharges} =
    useIsraeliVatEligibility(hotelId)

  const firstRoomNumberOfNights = calculateDateDifference(
    new Date(splitBookingDates.checkIn),
    new Date(splitBookingDates.splitDate)
  )

  const secondRoomNumberOfNights = calculateDateDifference(
    new Date(splitBookingDates.splitDate),
    new Date(splitBookingDates.checkOut)
  )

  const roomsNightsAmount = [firstRoomNumberOfNights, secondRoomNumberOfNights]

  const totalNumberOfNights = firstRoomNumberOfNights + secondRoomNumberOfNights
  const displayPrice = getDisplayPrice(prices)

  const {currencyCode: displayCurrencyCode, base: baseRateDisplayPrice} =
    displayPrice.chargeable

  const currencyCode = displayCurrencyCode || defaultCurrencyCode

  const roomPricePerNight = baseRateDisplayPrice / totalNumberOfNights

  const Content = () => (
    <>
      <RoomNamesWrapper container spacing="s000" gap="s400" direction="column">
        {splitBookingRooms.map((room, index) => {
          const numberOfNights = roomsNightsAmount[index]

          return (
            <div data-id={`splitBooking.roomNames.${index}`} key={index}>
              <Text variant="bodyM" as="span">
                {`1x ${room.name}`} <span>·</span>&nbsp;
              </Text>
              <Text variant="bodyM" as="span">
                <FormattedMessageWrapper
                  id="numberOfNights"
                  defaultMessage="{count, plural, one {1 night} other {# nights}}"
                  values={{count: numberOfNights}}
                />
              </Text>
            </div>
          )
        })}
      </RoomNamesWrapper>
      <Box paddingTop="s500" paddingBottom="s500">
        <Divider />
      </Box>
      <div data-id="splitBooking.roomPriceBreakdown">
        <RoomPrice
          currencyCode={currencyCode}
          numberOfNights={totalNumberOfNights}
          numberOfRooms={1}
          roomPricePerNight={roomPricePerNight}
          totalPricePerRoom={baseRateDisplayPrice}
          includeNumberOfRooms={false}
          isLoading={false}
          showAverageNightlyPriceText
        />
      </div>
      <PriceDetails
        showFeesDescriptions={false}
        checkIn={splitBookingDates.checkIn}
        checkOut={splitBookingDates.checkOut}
        prices={prices}
        numberOfRooms={roomsConfig.numberOfRooms}
        numberOfGuests={
          roomsConfig.numberOfAdults + roomsConfig.numberOfChildren
        }
        displayNumberOfRooms={false}
        hasAdditionalCharges={hasAdditionalCharges}
        hotelCountryCode={hotel?.country}
        isLocalVatIncluded={isLocalVatIncluded}
        showVatDisclaimer={isPropertyEligible}
      />
    </>
  )
  if (isMobile)
    return (
      <BottomSheet
        isOpen={isOpen}
        navBarProps={{centerContent: <Title />}}
        onClose={onClose}
        prefersReducedMotion={prefersReducedMotion}
      >
        <Box paddingTop="s500">
          <Content />
        </Box>
      </BottomSheet>
    )
  return (
    <PopUp
      navBarProps={{
        centerContent: <Title />
      }}
      isOpen={isOpen}
      closeAction={onClose}
      width={600}
    >
      <Box width="568px" padding="s000">
        <Content />
      </Box>
    </PopUp>
  )
}
